<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Users"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :searchValue="search"
      @update:searchValue="search = $event"
      @search="applyFilters"
      @refresh="loadData"
      @export="handleExport"
    />

    <div class="page-body">
      <div class="kpi-grid">
        <MetricCard label="Total Users" :value="summary.totalUsers" :state="summaryState" />
        <MetricCard label="Enabled Members" :value="summary.enabledMembers" :state="summaryState" />
        <MetricCard label="Disabled Members" :value="summary.disabledMembers" :state="summaryState" />
        <MetricCard label="Guests" :value="summary.guests" :state="summaryState" />
        <MetricCard label="Licensed Users" :value="summary.licensedUsers" :state="summaryState" />
        <MetricCard label="Unlicensed Enabled" :value="summary.unlicensedEnabled" :state="summaryState" />
        <MetricCard label="Cloud-Only" :value="summary.cloudOnly" :state="summaryState" />
        <MetricCard label="Synchronised" :value="summary.synchronised" :state="summaryState" />
        <MetricCard label="No Manager" :value="summary.noManager" :state="summaryState" />
        <MetricCard label="Created Last 30d" :value="summary.createdLast30Days" :state="summaryState" />
        <MetricCard label="Deleted Last 30d" :value="summary.deletedLast30Days" :state="summaryState" />
        <MetricCard label="Users with Findings" :value="summary.usersWithFindings" :state="summaryState" />
      </div>

      <div class="filters-bar">
        <div class="filter-group">
          <label class="filter-label">Status:</label>
          <select v-model="filters.status" class="filter-select" @change="applyFilters">
            <option value="">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">User Type:</label>
          <select v-model="filters.userType" class="filter-select" @change="applyFilters">
            <option value="">All</option>
            <option value="member">Member</option>
            <option value="guest">Guest</option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Quick Filter:</label>
          <select v-model="filters.preset" class="filter-select" @change="applyPreset">
            <option value="">Select...</option>
            <option value="licensed-disabled">Licensed &amp; Disabled</option>
            <option value="licensed-never-signed-in">Licensed &amp; Never Signed In</option>
            <option value="no-sign-in-90d">No Sign-in for 90 Days</option>
            <option value="no-sign-in-180d">No Sign-in for 180 Days</option>
            <option value="guest-inactive-180d">Guest Inactive for 180 Days</option>
            <option value="privileged-not-mfa">Privileged &amp; Not MFA Capable</option>
            <option value="direct-assignment">Directly Assigned Paid Licences</option>
            <option value="no-manager">No Manager</option>
            <option value="recently-created">Recently Created</option>
          </select>
        </div>
      </div>

      <div v-if="error" class="error-message">{{ error }}</div>

      <div v-else-if="tableState === 'loading'" class="loading-state">Loading users...</div>
      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No users found" description="No users match the current filters." />
      </div>
      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Type</th>
              <th>Status</th>
              <th>Source</th>
              <th>Department</th>
              <th>Job Title</th>
              <th>Licences</th>
              <th>Last Sign-in</th>
              <th>MFA Capable</th>
              <th>Privileged</th>
              <th>Findings</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.userId">
              <td>
                <div class="user-name">{{ user.displayName }}</div>
                <div class="user-upn">{{ user.userPrincipalName }}</div>
              </td>
              <td>{{ user.userType }}</td>
              <td>
                <span class="status-chip" :class="user.accountEnabled ? 'status-enabled' : 'status-disabled'">
                  {{ user.accountEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </td>
              <td>{{ user.onPremisesSyncEnabled ? 'Synced' : 'Cloud-only' }}</td>
              <td>{{ user.department || '--' }}</td>
              <td>{{ user.jobTitle || '--' }}</td>
              <td>
                <span v-if="user.licenceCount > 0" class="licence-badge">{{ user.licenceCount }}</span>
                <span v-else>--</span>
              </td>
              <td>{{ user.lastSignIn ? formatRelative(user.lastSignIn) : '--' }}</td>
              <td>
                <span :class="mfaClass(user.isMfaCapable)">
                  {{ mfaLabel(user.isMfaCapable) }}
                </span>
              </td>
              <td>
                <span v-if="user.isPrivileged" class="priv-badge">{{ user.highestRole || 'Yes' }}</span>
                <span v-else>--</span>
              </td>
              <td>
                <FindingSeverityChip v-if="user.findingCount > 0" :severity="user.worstSeverity" />
                <span v-else>--</span>
              </td>
              <td>
                <button class="detail-btn" @click="openUser(user.userId)">Details</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>

      <!-- User Detail Drawer (simplified) -->
      <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
        <div class="drawer">
          <div class="drawer-header">
            <h2>User Details</h2>
            <button class="drawer-close" @click="closeDrawer">&times;</button>
          </div>
          <div class="drawer-body">
            <div v-if="drawerState === 'loading'"><p>Loading...</p></div>
            <div v-else-if="drawerState === 'loaded'">
              <div class="drawer-tabs">
                <button v-for="tab in drawerTabs" :key="tab.key" class="drawer-tab" :class="{ active: activeTab === tab.key }" @click="activeTab = tab.key">{{ tab.label }}</button>
              </div>
              <div class="drawer-tab-content">
                <div v-if="activeTab === 'overview'" class="detail-grid">
                  <div class="detail-item"><span class="detail-label">Object ID</span><span class="detail-value">{{ selectedUser.objectId }}</span></div>
                  <div class="detail-item"><span class="detail-label">Display Name</span><span class="detail-value">{{ selectedUser.displayName }}</span></div>
                  <div class="detail-item"><span class="detail-label">UPN</span><span class="detail-value">{{ selectedUser.userPrincipalName }}</span></div>
                  <div class="detail-item"><span class="detail-label">Mail</span><span class="detail-value">{{ selectedUser.mail || '--' }}</span></div>
                  <div class="detail-item"><span class="detail-label">User Type</span><span class="detail-value">{{ selectedUser.userType }}</span></div>
                  <div class="detail-item"><span class="detail-label">Account Enabled</span><span class="detail-value">{{ selectedUser.accountEnabled ? 'Yes' : 'No' }}</span></div>
                  <div class="detail-item"><span class="detail-label">Created</span><span class="detail-value">{{ selectedUser.createdDateTime || '--' }}</span></div>
                  <div class="detail-item"><span class="detail-label">Department</span><span class="detail-value">{{ selectedUser.department || '--' }}</span></div>
                  <div class="detail-item"><span class="detail-label">Job Title</span><span class="detail-value">{{ selectedUser.jobTitle || '--' }}</span></div>
                  <div class="detail-item"><span class="detail-label">Usage Location</span><span class="detail-value">{{ selectedUser.usageLocation || '--' }}</span></div>
                </div>
                <div v-else-if="activeTab === 'licences'" class="tab-list">
                  <p v-if="!userTabData.licences || userTabData.licences.length === 0" class="tab-placeholder">No licences assigned.</p>
                  <div v-else v-for="lic in userTabData.licences" :key="lic.skuId" class="detail-item">
                    <span class="detail-label">{{ lic.skuPartNumber }}</span>
                    <span class="detail-value">{{ lic.assignmentSource || 'Unknown' }} · {{ lic.state || 'Active' }}</span>
                  </div>
                </div>
                <div v-else-if="activeTab === 'groups'" class="tab-list">
                  <p v-if="!userTabData.groups || userTabData.groups.length === 0" class="tab-placeholder">Not a member of any groups.</p>
                  <div v-else v-for="grp in userTabData.groups" :key="grp.groupId" class="detail-item">
                    <span class="detail-label">{{ grp.displayName }}</span>
                    <span class="detail-value">{{ grp.groupType }} · {{ grp.memberType }}</span>
                  </div>
                </div>
                <div v-else-if="activeTab === 'activity'" class="tab-list">
                  <p v-if="!userTabData.activity || !userTabData.activity.last_sign_in_date_time" class="tab-placeholder">No activity data available.</p>
                  <div v-else class="detail-grid">
                    <div class="detail-item"><span class="detail-label">Last Successful Sign-in</span><span class="detail-value">{{ userTabData.activity.last_successful_sign_in_date_time }}</span></div>
                    <div class="detail-item"><span class="detail-label">Last M365 Activity</span><span class="detail-value">{{ userTabData.activity.last_m365_activity_date || '--' }}</span></div>
                    <div class="detail-item"><span class="detail-label">Exchange Activity</span><span class="detail-value">{{ userTabData.activity.exchange_last_activity_date || '--' }}</span></div>
                    <div class="detail-item"><span class="detail-label">Teams Activity</span><span class="detail-value">{{ userTabData.activity.teams_last_activity_date || '--' }}</span></div>
                  </div>
                </div>
                <div v-else-if="activeTab === 'roles'" class="tab-list">
                  <p v-if="!userTabData.roles || userTabData.roles.length === 0" class="tab-placeholder">No directory roles assigned.</p>
                  <div v-else v-for="role in userTabData.roles" :key="role.roleId" class="detail-item">
                    <span class="detail-label">{{ role.displayName }}</span>
                    <span class="detail-value">{{ role.isPermanent ? 'Permanent' : 'Eligible' }}</span>
                  </div>
                </div>
                <div v-else-if="activeTab === 'findings'" class="tab-list">
                  <p v-if="!userTabData.findings || userTabData.findings.length === 0" class="tab-placeholder">No open findings for this user.</p>
                  <div v-else v-for="finding in userTabData.findings" :key="finding.findingId" class="finding-line">
                    <FindingSeverityChip :severity="finding.severity" />
                    <span>{{ finding.title }}</span>
                  </div>
                </div>
                <div v-else>
                  <p class="tab-placeholder">{{ activeTab }} data is not yet available.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import MetricCard from '@/components/entra/common/MetricCard.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import FindingSeverityChip from '@/components/entra/common/FindingSeverityChip.vue';
import { getEntraUsers, getEntraUserDetails, getEntraUserLicences, getEntraUserActivity, getEntraUserRoles, getEntraUserGroups, getEntraUserFindings } from '@/services/entra-api.js';
import { formatDate } from '@/services/date-utils.js';

var PRESET_PARAM_MAP = {
  'licensed-disabled': { licensed: 'true', accountEnabled: 'false' },
  'licensed-never-signed-in': { licensed: 'true', inactiveDays: 10950 },
  'no-sign-in-90d': { inactiveDays: 90 },
  'no-sign-in-180d': { inactiveDays: 180 },
  'guest-inactive-180d': { userType: 'Guest', inactiveDays: 180 },
  'direct-assignment': { licensed: 'true' },
  'recently-created': { inactiveDays: 0 }
};

function decomposePreset(presetValue) {
  return PRESET_PARAM_MAP[presetValue] || {};
}

export default {
  name: 'EntraUsers',
  components: { EntraPageHeader, MetricCard, EmptyState, FindingSeverityChip },
  data: function() {
    return {
      tenantId: '',
      tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null,
      capabilities: [],
      search: '',
      summary: { totalUsers: 0, enabledMembers: 0, disabledMembers: 0, guests: 0, licensedUsers: 0, unlicensedEnabled: 0, cloudOnly: 0, synchronised: 0, noManager: 0, createdLast30Days: 0, deletedLast30Days: 0, usersWithFindings: 0 },
      summaryState: 'loading',
      users: [],
      tableState: 'loading',
      filters: { status: '', userType: '', preset: '' },
      page: 1, totalPages: 1,
      error: '',
      showDrawer: false, drawerState: 'loading',
      selectedUser: {}, userTabData: {}, activeTab: 'overview',
      drawerTabs: [
        { key: 'overview', label: 'Overview' },
        { key: 'licences', label: 'Licences' },
        { key: 'activity', label: 'Activity' },
        { key: 'authentication', label: 'Authentication' },
        { key: 'roles', label: 'Roles' },
        { key: 'groups', label: 'Groups' },
        { key: 'findings', label: 'Findings' }
      ]
    };
  },
  methods: {
    formatRelative: function(iso) { return formatDate(iso, { mode: 'relative' }); },
    mfaClass: function(val) {
      if (val === true) return 'mfa-yes';
      if (val === false) return 'mfa-no';
      return 'mfa-unknown';
    },
    mfaLabel: function(val) {
      if (val === true) return 'Yes';
      if (val === false) return 'No';
      return 'Unknown';
    },
    loadData: function() {
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        this.tableState = 'empty';
        return;
      }
      this.loadUsers();
    },
    loadUsers: function() {
      var self = this;
      this.tableState = 'loading';
      var params = { page: this.page, pageSize: 50 };
      if (this.search) params.search = this.search;
      if (this.filters.status) params.accountEnabled = this.filters.status === 'enabled';
      if (this.filters.userType) params.userType = this.filters.userType;

      if (this.filters.preset) {
        var presetParams = decomposePreset(this.filters.preset);
        Object.assign(params, presetParams);
      }

      var promise = this.tenantId
        ? getEntraUsers(this.tenantId, params)
        : Promise.reject(new Error('No tenant selected. Please select a tenant from the portfolio.'));

      promise.then(function(data) {
        var users = data.items || [];
        self.users = users;
        self.totalPages = Math.ceil((data.total || 0) / 50);
        self.tableState = users.length === 0 ? 'empty' : 'loaded';
        self.summary = {
          totalUsers: data.total || 0,
          enabledMembers: users.filter(function(u) { return u.accountEnabled && u.userType === 'Member'; }).length,
          disabledMembers: users.filter(function(u) { return !u.accountEnabled && u.userType === 'Member'; }).length,
          guests: users.filter(function(u) { return u.userType === 'Guest'; }).length,
          licensedUsers: users.filter(function(u) { return u.licenceCount > 0; }).length,
          unlicensedEnabled: users.filter(function(u) { return u.accountEnabled && u.licenceCount === 0; }).length,
          cloudOnly: users.filter(function(u) { return !u.onPremisesSyncEnabled; }).length,
          synchronised: users.filter(function(u) { return u.onPremisesSyncEnabled; }).length,
          noManager: 0,
          createdLast30Days: 0,
          deletedLast30Days: 0,
          usersWithFindings: users.filter(function(u) { return u.findingCount > 0; }).length
        };
        self.summaryState = 'loaded';
      }).catch(function(err) {
        self.error = err.message;
        self.tableState = 'error';
      });
    },
    applyFilters: function() { this.page = 1; this.loadUsers(); },
    applyPreset: function() { this.page = 1; this.loadUsers(); },
    goToPage: function(newPage) { this.page = newPage; this.loadUsers(); },
    handleExport: function(format) { console.log('Export as ' + format); },
    openUser: function(userId) {
      var self = this;
      this.showDrawer = true;
      this.drawerState = 'loading';
      this.activeTab = 'overview';
      this.selectedUser = null;
      this.userTabData = {};

      getEntraUserDetails(this.tenantId, userId).then(function(data) {
        self.selectedUser = data;
        self.drawerState = 'loaded';
      }).catch(function() {
        self.drawerState = 'unavailable';
      });
    },
    loadUserTabData: function(tabKey) {
      var self = this;
      if (!this.selectedUser || !this.selectedUser.userId) return;
      var userId = this.selectedUser.userId;

      if (tabKey === 'licences') {
        getEntraUserLicences(this.tenantId, userId).then(function(data) {
          self.userTabData.licences = data.items || [];
        }).catch(function() { self.userTabData.licences = []; });
      } else if (tabKey === 'groups') {
        getEntraUserGroups(this.tenantId, userId).then(function(data) {
          self.userTabData.groups = data.items || [];
        }).catch(function() { self.userTabData.groups = []; });
      } else if (tabKey === 'activity') {
        getEntraUserActivity(this.tenantId, userId).then(function(data) {
          self.userTabData.activity = data;
        }).catch(function() { self.userTabData.activity = null; });
      } else if (tabKey === 'roles') {
        getEntraUserRoles(this.tenantId, userId).then(function(data) {
          self.userTabData.roles = data.items || [];
        }).catch(function() { self.userTabData.roles = []; });
      } else if (tabKey === 'findings') {
        getEntraUserFindings(this.tenantId, userId).then(function(data) {
          self.userTabData.findings = data.items || [];
        }).catch(function() { self.userTabData.findings = []; });
      }
    },
    closeDrawer: function() { this.showDrawer = false; }
  },
  mounted: function() { this.loadData(); },
  watch: {
    '$route.params.tenantId': 'loadData',
    activeTab: function(newTab) {
      this.loadUserTabData(newTab);
    }
  }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 24px; }

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.filters-bar {
  display: flex; gap: 16px; align-items: center; flex-wrap: wrap;
  margin-bottom: 20px; padding: 12px 16px;
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px;
}
.filter-group { display: flex; align-items: center; gap: 6px; }
.filter-label { font-size: 0.75rem; font-weight: 600; color: #64748b; white-space: nowrap; }
.filter-select { padding: 4px 8px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 0.8125rem; color: #334155; background: #ffffff; font-family: 'Inter', sans-serif; }

.error-message { padding: 16px; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; color: #991b1b; font-size: 0.875rem; }
.loading-state { padding: 48px; text-align: center; color: #94a3b8; }

.table-wrapper { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
.data-table th { background: #f8fafc; color: #475569; font-weight: 600; text-align: left; padding: 10px 12px; border-bottom: 2px solid #e2e8f0; white-space: nowrap; }
.data-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; }

.user-name { font-weight: 600; color: #1e293b; }
.user-upn { font-size: 0.6875rem; color: #94a3b8; }
.status-chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 0.6875rem; font-weight: 600; }
.status-enabled { background: #dcfce7; color: #166534; }
.status-disabled { background: #fee2e2; color: #991b1b; }
.licence-badge { background: #eff6ff; color: #2563eb; padding: 1px 6px; border-radius: 4px; font-size: 0.6875rem; font-weight: 600; }
.mfa-yes { color: #16a34a; font-weight: 600; }
.mfa-no { color: #dc2626; font-weight: 600; }
.mfa-unknown { color: #94a3b8; }
.priv-badge { background: #fef3c7; color: #92400e; padding: 1px 6px; border-radius: 4px; font-size: 0.6875rem; font-weight: 600; }
.detail-btn { background: none; border: 1px solid #cbd5e1; padding: 4px 10px; border-radius: 4px; font-size: 0.75rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.detail-btn:hover { background: #f1f5f9; }

.pagination { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 12px; border-top: 1px solid #e2e8f0; }
.page-btn { background: transparent; border: 1px solid #cbd5e1; padding: 6px 14px; border-radius: 6px; font-size: 0.8125rem; color: #475569; cursor: pointer; font-family: 'Inter', sans-serif; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-info { font-size: 0.8125rem; color: #64748b; }

/* Drawer */
.drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.3); z-index: 2000; display: flex; justify-content: flex-end; }
.drawer { width: 600px; max-width: 90vw; background: #ffffff; height: 100%; overflow-y: auto; box-shadow: -4px 0 16px rgba(0,0,0,0.1); }
.drawer-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #e2e8f0; }
.drawer-header h2 { font-size: 1.125rem; font-weight: 600; color: #1e293b; margin: 0; }
.drawer-close { background: none; border: none; font-size: 1.5rem; color: #94a3b8; cursor: pointer; }
.drawer-body { padding: 24px; }
.drawer-tabs { display: flex; gap: 2px; border-bottom: 1px solid #e2e8f0; margin-bottom: 20px; flex-wrap: wrap; }
.drawer-tab { padding: 8px 14px; background: none; border: none; border-bottom: 2px solid transparent; font-size: 0.8125rem; color: #64748b; cursor: pointer; font-family: 'Inter', sans-serif; }
.drawer-tab.active { color: #3b82f6; border-bottom-color: #3b82f6; font-weight: 600; }
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.detail-item { display: flex; flex-direction: column; gap: 2px; }
.detail-label { font-size: 0.6875rem; color: #94a3b8; font-weight: 600; text-transform: uppercase; }
.detail-value { font-size: 0.8125rem; color: #334155; word-break: break-all; }
.tab-placeholder { font-size: 0.8125rem; color: #94a3b8; font-style: italic; }
</style>
